import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  metaAdsFormSectionClass,
  metaAdsGradientBorderOnlyClass,
} from "./metaAdsTheme";

/** Block with gradient + pulse — subtle brand-aligned loading blocks */
function SkBlock({ className, delayClass = "" }) {
  return (
    <div
      className={cn(
        "from-border/55 via-muted/45 to-border/55 rounded-lg bg-gradient-to-r",
        "motion-safe:animate-pulse motion-reduce:animate-none",
        delayClass,
        className,
      )}
    />
  );
}

function PreviewSkeletonFrame({ compact = false }) {
  return (
    <div
      className={cn(metaAdsGradientBorderOnlyClass, "p-px", compact && "mt-2")}
    >
      <div
        className={cn(
          "bg-card/95 rounded-2xl p-4 backdrop-blur-sm",
          compact ? "min-h-[260px]" : "min-h-[320px]",
        )}
      >
        <div className="mb-4 flex items-center gap-2">
          <SkBlock className="size-11 shrink-0 rounded-full motion-safe:delay-75" />
          <div className="flex-1 space-y-2">
            <SkBlock className="h-3 w-28 motion-safe:delay-100" />
            <SkBlock className="h-2.5 w-full max-w-[9rem] motion-safe:delay-150" />
          </div>
        </div>
        <SkBlock
          className={cn(
            "mb-4 w-full rounded-xl motion-safe:delay-150",
            compact
              ? "aspect-[1.95/1] max-h-40"
              : "aspect-[10/13] max-h-56 sm:max-h-64",
          )}
        />
        <div className="space-y-2">
          <SkBlock className="h-3 w-full motion-safe:delay-200" />
          <SkBlock className="h-3 w-full max-w-[80%] motion-safe:delay-200" />
          <SkBlock className="mt-3 h-8 w-24 rounded-lg motion-safe:delay-300" />
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page skeleton for Meta Ads edit campaign — mirrors loaded form grid
 * (stacked sections + desktop preview column) to avoid layout jump.
 */
export function MetaAdsEditCampaignSkeleton({ className }) {
  const { t } = useTranslation("metaAds");

  return (
    <div
      className={cn("space-y-3", className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="sr-only">{t("create.editLoading")}</span>

      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <SkBlock className="h-9 w-52 sm:h-10 sm:w-64" />
          {/* Accent echoes title gradient without fighting SkBlock base */}
          <div
            className={cn(
              "h-9 w-32 rounded-lg sm:h-10 sm:w-44",
              "from-brand-cyan/35 via-muted/35 to-brand-purple/22 bg-gradient-to-br",
              "motion-safe:animate-pulse motion-safe:delay-75 motion-reduce:animate-none",
            )}
            aria-hidden
          />
        </div>
        <div className="space-y-2">
          <SkBlock className="h-4 w-full max-w-xl motion-safe:delay-75" />
          <SkBlock className="h-4 w-full max-w-lg motion-safe:delay-150" />
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          <section className={metaAdsFormSectionClass}>
            <SkBlock className="mb-4 h-2.5 w-28 rounded-sm motion-safe:delay-75" />
            <SkBlock className="mb-4 h-3 w-full max-w-md motion-safe:delay-100" />
            <SkBlock className="mb-2 h-3 w-24 motion-safe:delay-150" />
            <SkBlock className="h-12 w-full rounded-xl motion-safe:delay-150" />
          </section>

          <section className={metaAdsFormSectionClass}>
            <SkBlock className="mb-4 h-2.5 w-20 rounded-sm motion-safe:delay-75" />
            <SkBlock className="mb-3 h-3 w-full max-w-[11rem] motion-safe:delay-100" />
            {[0, 1, 2].map((i) => (
              <SkBlock
                key={i}
                className={cn(
                  "mb-3 h-[4.25rem] w-full rounded-xl last:mb-0",
                  i === 0 && "motion-safe:delay-100",
                  i === 1 && "motion-safe:delay-150",
                  i === 2 && "motion-safe:delay-200",
                )}
              />
            ))}
          </section>

          <section className={metaAdsFormSectionClass}>
            <SkBlock className="mb-4 h-2.5 w-36 rounded-sm" />
            <SkBlock className="mb-4 h-3 w-full max-w-[22rem] motion-safe:delay-75" />
            <SkBlock className="mb-2 h-3 w-28 motion-safe:delay-100" />
            <SkBlock className="h-12 w-full rounded-xl motion-safe:delay-100" />
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <SkBlock className="h-[5.75rem] w-full rounded-xl motion-safe:delay-150 sm:rounded-2xl" />
              <SkBlock className="h-[5.75rem] w-full rounded-xl motion-safe:delay-200 sm:rounded-2xl" />
            </div>
          </section>

          <section className={metaAdsFormSectionClass}>
            <SkBlock className="mb-4 h-2.5 w-24 rounded-sm" />
            <SkBlock className="mb-2 h-3 w-20 motion-safe:delay-75" />
            <SkBlock className="mb-4 h-12 w-full rounded-xl motion-safe:delay-75" />
            <SkBlock className="mb-2 h-3 w-24 motion-safe:delay-100" />
            <SkBlock className="mb-6 min-h-[7.5rem] w-full rounded-md motion-safe:delay-100" />
            <SkBlock className="mb-2 h-3 w-36 motion-safe:delay-150" />
            <SkBlock className="h-12 w-full rounded-xl motion-safe:delay-150" />
            <SkBlock className="mt-4 h-24 w-full rounded-xl motion-safe:delay-200" />
          </section>

          <SkBlock className="h-[3.25rem] w-full rounded-xl motion-safe:delay-300" />

          <div className="lg:hidden">
            <PreviewSkeletonFrame compact />
          </div>
        </div>

        <div className="hidden lg:col-span-1 lg:block">
          <PreviewSkeletonFrame />
        </div>
      </div>
    </div>
  );
}

export default MetaAdsEditCampaignSkeleton;
